import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { toast } from "react-toastify";
import { FiSave, FiPercent, FiClock, FiTag, FiInfo } from "react-icons/fi";
import { Button, Card, CardBody, PageHeader, Skeleton } from "./ui";

export default function PricingPanel() {
    const [pricing, setPricing] = useState({
        '3dY01': { days: 3, price: 15000, promo: 0 },
        '1gT0a': { days: 7, price: 30000, promo: 0 },
        'h4Jwq': { days: 30, price: 100000, promo: 0 },
        '9Kp2m': { days: 90, price: 270000, promo: 0 },
        'xR7vL': { days: 180, price: 450000, promo: 0 },
        'bN5qZ': { days: 365, price: 750000, promo: 0 }
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchPricing();
    }, []);

    const fetchPricing = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('pricing')
                .select('*');

            if (error) throw error;

            if (data && data.length > 0) {
                const pricingMap = {};
                data.forEach(item => {
                    pricingMap[item.expiry_code] = {
                        days: item.days,
                        price: item.price,
                        promo: item.promo_discount || 0
                    };
                });
                setPricing(prev => ({ ...prev, ...pricingMap }));
            }
        } catch (error) {
            console.error('Error fetching pricing:', error);
            toast.error('Gagal load pricing data');
        } finally {
            setLoading(false);
        }
    };

    const handlePriceChange = (code, field, value) => {
        setPricing(prev => ({
            ...prev,
            [code]: {
                ...prev[code],
                [field]: parseInt(value) || 0
            }
        }));
    };

    const calculateFinalPrice = (code) => {
        const item = pricing[code];
        const discount = (item.price * item.promo) / 100;
        return item.price - discount;
    };

    const savePricing = async () => {
        setSaving(true);
        try {
            const pricingData = Object.entries(pricing).map(([code, data]) => ({
                expiry_code: code,
                days: data.days,
                price: data.price,
                promo_discount: data.promo,
                final_price: calculateFinalPrice(code),
                updated_at: new Date().toISOString()
            }));

            for (const item of pricingData) {
                const { error } = await supabase
                    .from('pricing')
                    .upsert(item, { onConflict: 'expiry_code' });

                if (error) throw error;
            }

            toast.success('✅ Pricing berhasil disimpan!');
        } catch (error) {
            console.error('Error saving pricing:', error);
            toast.error('❌ Gagal menyimpan pricing');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-9 w-56 rounded-lg bg-slate-200 dark:bg-slate-800" />
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="h-64 w-full rounded-2xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader
                title="Pricing Management"
                subtitle="Kelola harga lisensi berdasarkan durasi"
                icon={FiTag}
                actions={
                    <Button icon={FiSave} loading={saving} onClick={savePricing}>
                        {saving ? "Menyimpan..." : "Simpan Pricing"}
                    </Button>
                }
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Object.entries(pricing).map(([code, data]) => {
                    const finalPrice = calculateFinalPrice(code);
                    const discountAmount = (data.price * data.promo) / 100;
                    return (
                        <Card key={code} className="transition-all duration-200 hover:shadow-card-hover">
                            <CardBody>
                                {/* Header */}
                                <div className="mb-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                                            <FiClock className="h-5 w-5" />
                                        </span>
                                        <div>
                                            <h3 className="font-bold text-slate-900 dark:text-slate-100">{data.days} Hari</h3>
                                            <p className="font-mono text-[11px] text-slate-400">{code}</p>
                                        </div>
                                    </div>
                                    {data.promo > 0 && <span className="rounded-full bg-success-50 px-2.5 py-1 text-xs font-semibold text-success-700 dark:bg-success-500/10 dark:text-success-400">-{data.promo}%</span>}
                                </div>

                                {/* Inputs */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-300">Harga Normal</label>
                                        <div className="relative">
                                            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">Rp</span>
                                            <input
                                                type="number"
                                                value={data.price}
                                                onChange={(e) => handlePriceChange(code, 'price', e.target.value)}
                                                className="field-base pl-10"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            <FiPercent className="mr-1 inline h-3.5 w-3.5" />
                                            Diskon Promo (%)
                                        </label>
                                        <input
                                            type="number"
                                            value={data.promo}
                                            onChange={(e) => handlePriceChange(code, 'promo', e.target.value)}
                                            min="0"
                                            max="100"
                                            className="field-base"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                {/* Final price */}
                                <div className="mt-4 rounded-xl bg-slate-50 p-4 dark:bg-slate-800/50">
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Harga Final</p>
                                    <p className={`mt-0.5 text-2xl font-bold ${data.promo > 0 ? 'text-success-600 dark:text-success-400' : 'text-slate-900 dark:text-white'}`}>
                                        Rp {finalPrice.toLocaleString('id-ID')}
                                    </p>
                                    {data.promo > 0 && (
                                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                            <span className="line-through">Rp {data.price.toLocaleString('id-ID')}</span>
                                            <span className="ml-1.5 font-medium text-success-600 dark:text-success-400">
                                                Hemat Rp {discountAmount.toLocaleString('id-ID')}
                                            </span>
                                        </p>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    );
                })}
            </div>

            <Card className="border-primary-100 bg-primary-50/60 dark:border-primary-500/20 dark:bg-primary-500/10">
                <CardBody className="flex gap-3">
                    <FiInfo className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    <div className="text-sm text-primary-800 dark:text-primary-200">
                        <p className="mb-1 font-semibold">Informasi</p>
                        <ul className="list-inside list-disc space-y-0.5 text-primary-700/90 dark:text-primary-300/90">
                            <li>Harga ditampilkan saat user memilih durasi lisensi</li>
                            <li>Diskon promo otomatis teraplikasi ke harga final</li>
                            <li>Perubahan akan langsung tersimpan ke database</li>
                            <li>Expiry code digunakan untuk generate lisensi</li>
                        </ul>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}
